import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { login } from "../../redux/features/authSlice";
import axios from 'axios';
import { ArrowRight, Eye, EyeOff, Shield, Zap, Database, User, MapPin, Clock, CheckCircle } from "lucide-react";
import { API_BASE_URL } from "../../config/api.js";
import { usePageTracking } from "../../hooks/usePageTracking";
import { cacheVisitorEmail } from "../../utils/visitorEmail";
import {
  trackCtaClick,
  trackFormSubmit,
  trackFormSuccess,
  trackFormError
} from "../../utils/googleAnalytics";
import { applyLanguageFromCountry } from "../../utils/languageFromCountry";
import { getJobsLandingPath } from "../../utils/routingUtils";

const REMEMBER_LOGIN_KEY = 'spintaskerRememberLogin';

const readRememberedLogin = () => {
  try {
    const raw = localStorage.getItem(REMEMBER_LOGIN_KEY);
    if (!raw) return { email: '', rememberMe: false };
    const parsed = JSON.parse(raw);
    const email = typeof parsed?.email === 'string' ? parsed.email.trim() : '';
    return { email, rememberMe: parsed?.rememberMe === true && !!email };
  } catch {
    return { email: '', rememberMe: false };
  }
};

const persistRememberedLogin = (email) => {
  localStorage.setItem(
    REMEMBER_LOGIN_KEY,
    JSON.stringify({ email: String(email || '').trim(), rememberMe: true })
  );
};

const clearRememberedLogin = () => {
  localStorage.removeItem(REMEMBER_LOGIN_KEY);
};

const LoginUnified = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const roleFromUrl = searchParams.get("role");
  const isDemo = searchParams.get("demo") === "true";
  const rememberedLogin = readRememberedLogin();
  const [userType, setUserType] = useState(roleFromUrl === "worker" ? "worker" : "company"); // "company" or "worker"
  const [rememberMe, setRememberMe] = useState(
    !isDemo && roleFromUrl !== "worker" ? rememberedLogin.rememberMe : false
  );

  // Track page visit
  usePageTracking('login');

  useEffect(() => {
    // Update userType when URL role changes
    if (roleFromUrl === "worker") {
      setUserType("worker");
      // Set demo worker data if in demo mode
      if (isDemo) {
        setFormData({ email: "demo.radnik@gmail.com", password: "spintaskerdemo" });
      }
    } else {
      setUserType("company");
      // Set demo company data if in demo mode
      if (isDemo) {
        setFormData({ email: "demo@gmail.com", password: "spintaskerdemo" });
      }
    }
  }, [roleFromUrl, isDemo]);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: isDemo && roleFromUrl !== "worker"
      ? "demo@gmail.com"
      : (isDemo && roleFromUrl === "worker"
        ? "demo.radnik@gmail.com"
        : rememberedLogin.email),
    password: isDemo ? "spintaskerdemo" : "",
  });

  const validateField = (name, value) => {
    let tempErrors = { ...errors };
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      tempErrors.email = emailRegex.test(value)
        ? ""
        : userType === "company" 
          ? "Unesite validnu email adresu" 
          : "Unesite validnu email adresu";
    }
    setErrors(tempErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'email') cacheVisitorEmail(value);
    validateField(name, value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    if (!formData.email || !formData.password) {
      toast.error("Email i lozinka su obavezni");
      return;
    }

    if (errors.email) {
      toast.error("Unesite validnu email adresu");
      return;
    }

    setIsSubmitting(true);
    trackFormSubmit('login', { user_type: userType, demo: isDemo });

    try {
      if (userType === "company") {
        // Company/Admin login
        await dispatch(login({
          formData: { ...formData, rememberMe },
          navigate,
          onSuccess: () => {
            // Toast message is already shown in authSlice
          }
        })).unwrap();
        if (rememberMe) {
          persistRememberedLogin(formData.email);
        } else {
          clearRememberedLogin();
        }
        trackFormSuccess('login', { user_type: 'company', demo: isDemo });
      } else {
        // Worker login
        console.log('🔐 Frontend: Attempting worker login...');
        console.log('  Email:', formData.email);
        console.log('  API URL:', `${API_BASE_URL}/api/worker/login`);
        
        const { data } = await axios.post(
          `${API_BASE_URL}/api/worker/login`,
          {
            email: formData.email,
            password: formData.password
          }
        );

        console.log('✅ Frontend: Login successful!');
        console.log('  Worker:', data.result?.firstName, data.result?.lastName);

        localStorage.setItem("profile", JSON.stringify(data));
        localStorage.setItem("userType", "worker");
        localStorage.setItem("workerPermissions", JSON.stringify(data.result.permissions || {}));

        const companySlug = data.result?.companySlug || localStorage.getItem('currentCompanySlug');
        if (companySlug) {
          localStorage.setItem('currentCompanySlug', companySlug);
          if (data.result?.companyId) {
            localStorage.setItem('currentCompanyId', String(data.result.companyId));
          }
          if (data.result?.companyName) {
            localStorage.setItem('currentCompanyName', data.result.companyName);
          }
        }

        if (data.result?.businessType) {
          sessionStorage.setItem('businessType', data.result.businessType);
        }

        applyLanguageFromCountry(data.result?.countryCode);

        const userDataWithType = {
          ...data,
          userType: "worker"
        };

        dispatch({
          type: 'auth/login/fulfilled',
          payload: userDataWithType
        });

        const finalUserType = localStorage.getItem('userType');
        if (finalUserType !== 'worker') {
          console.error('🚨 SECURITY ERROR: userType was changed by Redux! Restoring...');
          localStorage.setItem("userType", "worker");
        }

        toast.success(`Dobrodošli, ${data.result.firstName} ${data.result.lastName}!`);
        
        navigate(getJobsLandingPath(companySlug), { replace: true });
        trackFormSuccess('login', { user_type: 'worker', demo: isDemo });
      }
    } catch (error) {
      console.error("Login error:", error);
      trackFormError('login', {
        user_type: userType,
        demo: isDemo,
        message: error?.response?.data?.message || error?.message || 'unknown'
      });
      if (userType === "worker") {
        const errorMessage = error?.response?.data?.message || "Neispravan email ili lozinka";
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = () => {
    if (userType === "company") {
      trackCtaClick('register', { cta_location: 'login_page' });
      navigate("/auth?role=company&type=register");
    } else {
      // Workers are typically registered by admins, but you can add a route if needed
      toast.info("Kontaktirajte administratora za kreiranje naloga");
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-900 flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-green-600 p-3 rounded-full">
                {userType === "company" ? (
                  <Database className="h-8 w-8 text-white" />
                ) : (
                  <User className="h-8 w-8 text-white" />
                )}
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              {userType === "company" ? "Dobrodošli nazad" : "Login za servisere"}
            </h2>
            <p className="text-gray-300">
              {userType === "company" 
                ? "Prijavite se na vaš SpinTasker nalog" 
                : "Prijavite se sa vašim servisnim nalogom"}
            </p>
          </div>

          {/* User Type Switch */}
          <div className="flex items-center justify-center bg-gray-800 rounded-lg p-1">
            <button
              type="button"
              onClick={() => {
                setUserType("company");
                // Restore demo data if in demo mode
                if (isDemo) {
                  setFormData({ email: "demo@gmail.com", password: "spintaskerdemo" });
                } else {
                  setFormData({ email: "", password: "" });
                }
                setErrors({});
              }}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                userType === "company"
                  ? "bg-green-600 text-white"
                  : "text-gray-300 hover:text-white"
              }`}
            >
              Admin / Kompanija
            </button>
            <button
              type="button"
              onClick={() => {
                setUserType("worker");
                // Set demo worker data if in demo mode
                if (isDemo) {
                  setFormData({ email: "demo.radnik@gmail.com", password: "spintaskerdemo" });
                } else {
                  setFormData({ email: "", password: "" });
                }
                setErrors({});
              }}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                userType === "worker"
                  ? "bg-green-600 text-white"
                  : "text-gray-300 hover:text-white"
              }`}
            >
              Serviser
            </button>
          </div>

          {/* Demo Mode Info */}
          {isDemo && (
            <div className="mt-6 p-4 bg-blue-900/30 border border-blue-500/50 rounded-lg">
              <p className="text-sm text-blue-300">
                <strong>Demo nalog:</strong> Kredencijali su automatski popunjeni. Kliknite na "Prijavi se" da pristupite demo verziji aplikacije.
                {userType === "worker" && (
                  <span className="block mt-2 text-xs text-blue-200">
                    Email: demo.radnik@gmail.com | Lozinka: spintaskerdemo
                  </span>
                )}
                {userType === "company" && (
                  <span className="block mt-2 text-xs text-blue-200">
                    Email: demo@gmail.com | Lozinka: spintaskerdemo
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Login Form */}
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="space-y-4">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email adresa
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                    errors.email ? 'border-red-500' : 'border-gray-600'
                  }`}
                  placeholder="Unesite vaš mejl"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Lozinka
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors pr-12"
                    placeholder={userType === "company" ? "Unesite vašu lozinku" : "Unesite vašu lozinku"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            {userType === "company" && (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-600 rounded bg-gray-800"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300">
                    Zapamti me
                  </label>
                </div>
                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    onClick={() => trackCtaClick('forgot_password', { cta_location: 'login_page' })}
                    className="text-green-400 hover:text-green-300 font-medium"
                  >
                    Zaboravljena lozinka?
                  </Link>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {userType === "company" ? "Prijavljivanje..." : "Prijavljivanje..."}
                </div>
              ) : (
                <div className="flex items-center">
                  Prijavi se
                  <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              )}
            </button>

            {/* Register Link */}
            {userType === "company" && (
              <div className="text-center">
                <p className="text-sm text-gray-300">
                  Nemate nalog?{" "}
                  <button
                    type="button"
                    onClick={handleRegister}
                    className="text-green-400 hover:text-green-300 font-medium"
                  >
                    Registrujte se besplatno
                  </button>
                </p>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Right Side - Features */}
      <div className="hidden lg:flex lg:flex-1 bg-green-600 p-12 flex-col justify-center text-white">
        <div className="max-w-md">
          {userType === "company" ? (
            <>
              <h3 className="text-3xl font-bold mb-6">
                Upravljajte svojim terenskim poslovima
              </h3>
              <p className="text-white mb-8 text-lg">
                Jednostavna i efikasna platforma za upravljanje terenskim servisima. 
                Kreirajte poslove, dodelite poslove serviserima i pratite napredak u realnom vremenu.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                    <Shield className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Sigurno i pouzdano</h4>
                    <p className="text-white text-sm">
                      Vaši podaci su zaštićeni i bezbedni
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Praćenje u realnom vremenu</h4>
                    <p className="text-white text-sm">
                      Pratite vremena vaših servisera i poslova
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                    <Database className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Sve na jednom mestu</h4>
                    <p className="text-white text-sm">
                      Upravljajte poslovima, serviserima i klijentima iz jedne aplikacije
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-3xl font-bold mb-6">
                Terenski serviseri
              </h3>
              <p className="text-white mb-8 text-lg">
                Pristupite dodeljenim poslovima, pratite svoje vreme i ažurirajte status 
                u realnom vremenu. Ostanite povezani sa svojim timom i klijentima.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Praćenje u realnom vremenu</h4>
                    <p className="text-white text-sm">
                      Pratite svoju lokaciju i napredak posla u realnom vremenu
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Upravljanje vremenom</h4>
                    <p className="text-white text-sm">
                      Pratite vreme putovanja, radno vreme i pauze automatski
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                    <CheckCircle className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Ažuriranje poslova</h4>
                    <p className="text-white text-sm">
                      Ažurirajte status posla i komunicirajte sa svojim timom
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginUnified;

